import React, { useState, useMemo } from 'react';
import { Product, CartItem, UnitType } from '../types';
import { Search, Plus, Minus, Trash2, ShoppingCart, RefreshCcw, DollarSign } from 'lucide-react';

interface POSProps {
  products: Product[];
  onCompleteSale: (items: CartItem[], total: number, method: string) => void;
}

const POS: React.FC<POSProps> = ({ products, onCompleteSale }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [discountType, setDiscountType] = useState<'RS' | '%'>('RS');
  const [discountValue, setDiscountValue] = useState(0);
  const [cashReceived, setCashReceived] = useState(0);

  const filteredProducts = useMemo(() => {
    return products.filter(p => 
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
      p.category.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [products, searchTerm]);

  const addToCart = (product: Product) => {
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item => 
          item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prev, { ...product, quantity: 1, saleUnit: 'Unit' }];
    });
  };

  const updateQuantity = (id: string, delta: number) => {
    setCart(prev => prev.map(item => {
      if (item.id === id) {
        const newQty = item.quantity + delta;
        return newQty > 0 ? { ...item, quantity: newQty } : null;
      }
      return item;
    }).filter(Boolean) as CartItem[]);
  };

  const removeFromCart = (id: string) => {
    setCart(prev => prev.filter(item => item.id !== id));
  };

  const getItemPrice = (item: CartItem) => item.priceUnit;

  const subTotal = cart.reduce((sum, item) => sum + (getItemPrice(item) * item.quantity), 0);

  const discountAmount = useMemo(() => {
    if (discountType === 'RS') {
      return Math.min(discountValue, subTotal);
    }
    return (subTotal * Math.min(discountValue, 100)) / 100;
  }, [discountValue, discountType, subTotal]);

  const grandTotal = subTotal - discountAmount;

  const clearOrder = () => {
    setCart([]);
    setDiscountValue(0);
    setCashReceived(0);
  };

  const handleCheckout = () => {
    if (cart.length === 0) return;
    onCompleteSale(cart, grandTotal, 'Cash');
    // In a real app, a receipt modal would show here. For now, just clear.
    clearOrder();
  };
  
  return (
    <div className="flex h-full gap-6">
      {/* Left Pane: Product Selection */}
      <div className="w-2/5 bg-white rounded-2xl shadow-sm border border-slate-200 flex flex-col">
        <div className="p-4 border-b border-slate-200">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
            <input 
              type="text" 
              placeholder="Search by name or category..." 
              className="w-full pl-12 pr-4 py-3 bg-slate-50 border-transparent rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all font-medium"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-3 gap-4">
          {filteredProducts.map(product => (
            <button 
              key={product.id}
              onClick={() => addToCart(product)}
              disabled={product.stock <= 0}
              className="flex flex-col p-3 bg-slate-50/70 border border-slate-200/50 rounded-xl transition-all text-left group relative hover:bg-emerald-50 hover:border-emerald-200 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <h4 className="font-bold text-slate-800 text-sm leading-tight line-clamp-2 h-10">{product.name}</h4>
              <p className="text-[10px] text-slate-400 mb-2 uppercase font-semibold tracking-wider">{product.category}</p>
              <div className="mt-auto w-full space-y-2 text-xs">
                <div className="flex justify-between items-center text-slate-500 font-medium">
                    <span>UNIT</span>
                    <span className="font-bold text-slate-700">RS. {product.priceUnit.toFixed(2)}</span>
                </div>
                 <div className="flex justify-between items-center text-slate-500 font-medium">
                    <span>STRIP</span>
                    <span className="font-bold text-slate-700">RS. {product.priceStrip.toFixed(2)}</span>
                </div>
                <div className="pt-2 border-t border-slate-200 flex items-center justify-between">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${product.stock < 50 ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-600'}`}>
                    {product.stock > 0 ? `${product.stock} UN` : 'OUT OF STOCK'}
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Right Pane: Active Order */}
      <div className="w-3/5 bg-white rounded-2xl shadow-sm border border-slate-200 flex flex-col">
        <div className="p-4 border-b border-slate-200">
          <h3 className="font-bold text-lg text-slate-800 flex items-center gap-2">
            <ShoppingCart size={20} className="text-emerald-600" />
            Active Order
          </h3>
        </div>
        
        {/* Cart Items Table */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-4">
            {/* Table Header */}
            <div className="grid grid-cols-12 gap-4 text-xs font-bold text-slate-400 uppercase tracking-wider py-3 border-b border-slate-200 sticky top-0 bg-white">
              <div className="col-span-4">Product</div>
              <div className="col-span-2 text-right">Rate</div>
              <div className="col-span-1 text-center">Unit</div>
              <div className="col-span-3 text-center">Quantity</div>
              <div className="col-span-2 text-right">Amount</div>
            </div>
            {/* Table Body */}
            {cart.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-slate-400 py-20">
                <p className="font-semibold">NO ITEMS IN CART</p>
              </div>
            ) : (
              cart.map(item => (
                <div key={item.id} className="grid grid-cols-12 gap-4 items-center py-3 border-b border-slate-100 group">
                  <div className="col-span-4 font-bold text-slate-800 text-sm">{item.name}</div>
                  <div className="col-span-2 text-right font-medium text-slate-600 text-sm">RS. {getItemPrice(item).toFixed(2)}</div>
                  <div className="col-span-1 text-center text-xs text-slate-500">{item.saleUnit}</div>
                  <div className="col-span-3 flex items-center justify-center gap-2">
                    <button onClick={() => updateQuantity(item.id, -1)} className="p-1.5 hover:bg-slate-100 rounded text-slate-500"><Minus size={14} /></button>
                    <span className="w-8 text-center text-sm font-bold text-slate-900">{item.quantity}</span>
                    <button onClick={() => updateQuantity(item.id, 1)} className="p-1.5 hover:bg-slate-100 rounded text-slate-500"><Plus size={14} /></button>
                    <button onClick={() => removeFromCart(item.id)} className="text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className="col-span-2 text-right font-bold text-emerald-600 text-sm">RS. {(getItemPrice(item) * item.quantity).toFixed(2)}</div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Order Footer */}
        <div className="p-5 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Overall Discount</label>
              <div className="flex">
                <input 
                  type="number"
                  value={discountValue || ''}
                  onChange={(e) => setDiscountValue(parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-l-lg focus:ring-2 focus:ring-emerald-500 outline-none text-sm font-semibold"
                />
                <button 
                  onClick={() => setDiscountType('RS')} 
                  className={`px-3 border-y border-r ${discountType === 'RS' ? 'bg-slate-700 text-white' : 'bg-white text-slate-500'} transition-colors text-xs font-bold`}
                >RS</button>
                <button 
                  onClick={() => setDiscountType('%')} 
                  className={`px-3 border-y border-r rounded-r-lg ${discountType === '%' ? 'bg-slate-700 text-white' : 'bg-white text-slate-500'} transition-colors text-xs font-bold`}
                >%</button>
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Cash Received</label>
              <input 
                type="number"
                value={cashReceived || ''}
                onChange={(e) => setCashReceived(parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none text-sm font-semibold"
              />
            </div>
            <div className="text-right">
              <p className="text-xs font-bold text-slate-500">
                ITEMS: {cart.length} &bull; SUB: RS. {subTotal.toFixed(2)}
              </p>
              <p className="text-xs font-bold text-red-500 mb-1">
                DISCOUNT: RS. {discountAmount.toFixed(2)}
              </p>
              <p className="text-slate-500 font-bold">GRAND TOTAL</p>
              <p className="text-4xl font-black text-emerald-600">RS. {grandTotal.toFixed(2)}</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={clearOrder}
              className="w-1/3 flex items-center justify-center gap-2 py-3 bg-slate-200 text-slate-700 rounded-lg font-bold hover:bg-slate-300 transition-colors"
            >
              <RefreshCcw size={16} />
              Cancel Order
            </button>
            <button
              onClick={handleCheckout}
              disabled={cart.length === 0}
              className="w-2/3 flex items-center justify-center gap-3 py-3 bg-emerald-600 text-white rounded-lg font-bold shadow-lg shadow-emerald-600/20 hover:bg-emerald-700 active:scale-[0.99] transition-all disabled:opacity-50"
            >
              <DollarSign size={18} />
              Confirm & Pay Cash
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default POS;